import utilService from '../../common/services/utilService';
import { chartColors } from '../consts/consts';

export default { getChartStyles, getOverviewChartData, getTrendlineEq, trendStartEnd, panelAxisMinMax };


function getOverviewChartData(overviewData, privateFilter) {
    let chartData = { labels: [], datasets: [] };
    
    // Safety check
    if (!overviewData || !overviewData.visits || !Array.isArray(overviewData.visits) || overviewData.visits.length === 0) {
        return chartData;
    }
    
    // Build a map of time -> year from items that have a year property
    // This allows us to infer the year for items that don't have it
    const timeToYearMap = new Map();
    const filterKeys = Object.keys(privateFilter);
    
    // First pass: collect all items with year to build the time->year mapping
    const collectItemsWithYear = (array) => {
        if (array && Array.isArray(array)) {
            array.forEach(item => {
                if (item && item.time !== null && item.time !== undefined && item.year) {
                    const timeKey = item.time.toString();
                    const yearStr = item.year.toString().slice(2, 4);
                    // Store the year for this time value (if multiple years exist for same time, keep the first one)
                    if (!timeToYearMap.has(timeKey)) {
                        timeToYearMap.set(timeKey, yearStr);
                    }
                }
            });
        }
    };
    
    // Collect years from all metrics
    filterKeys.forEach(key => {
        if (overviewData[key] && privateFilter[key]) {
            collectItemsWithYear(overviewData[key]);
        }
    });
    
    // Also check visits and complaints
    if (overviewData.visits) {
        collectItemsWithYear(overviewData.visits);
    }
    if (overviewData.complaints && privateFilter.complaints_vs_visits) {
        collectItemsWithYear(overviewData.complaints);
    }
    
    // Create a normalized date key function that uses year from item or looks it up
    const getDateKey = (item) => {
        const { time, year } = item;
        let yearStr = null;
        
        if (year) {
            // Item has year, use it
            yearStr = year.toString().slice(2, 4);
        } else {
            // Item doesn't have year, try to find it from the map
            const timeKey = time.toString();
            yearStr = timeToYearMap.get(timeKey);
        }
        
        // Only return a date key if we have both time and year
        if (time !== null && time !== undefined && yearStr) {
            return `${time}/${yearStr}`;
        }
        // Return null if we can't determine the year (this item will be excluded)
        return null;
    };
    
    // Build a unified set of all dates from all selected metrics using normalized keys
    // Only include dates where we can determine the year (from item or map)
    const allDatesSet = new Set();
    
    // Collect all dates from selected metrics
    filterKeys.forEach(key => {
        if (overviewData[key] && privateFilter[key] && Array.isArray(overviewData[key])) {
            overviewData[key].forEach(item => {
                if (item && (item.time !== null && item.time !== undefined)) {
                    const dateKey = getDateKey(item);
                    if (dateKey) {
                        allDatesSet.add(dateKey);
                    }
                }
            });
        }
    });
    
    // Also include dates from visits (base array) and complaints (for complaints_vs_visits)
    if (overviewData.visits) {
        overviewData.visits.forEach(item => {
            if (item && (item.time !== null && item.time !== undefined)) {
                const dateKey = getDateKey(item);
                if (dateKey) {
                    allDatesSet.add(dateKey);
                }
            }
        });
    }
    if (overviewData.complaints && privateFilter.complaints_vs_visits) {
        overviewData.complaints.forEach(item => {
            if (item && (item.time !== null && item.time !== undefined)) {
                const dateKey = getDateKey(item);
                if (dateKey) {
                    allDatesSet.add(dateKey);
                }
            }
        });
    }
    
    // Convert to sorted array of labels
    // Sort by parsing the date string to ensure chronological order
    const allDates = Array.from(allDatesSet).sort((a, b) => {
        // Parse date strings like "1/11", "26/10", "1/11/24", or "26/10/24" for comparison
        const parseDate = (dateStr) => {
            const parts = dateStr.split('/').map(p => parseInt(p));
            if (parts.length === 2) {
                // Format: "day/month" or could be "time/year" - check if second part is > 12 (likely year)
                if (parts[1] > 12) {
                    // Format: "time/year" where time might be day or a number
                    return { day: parts[0], month: 1, year: parts[1] < 100 ? 2000 + parts[1] : parts[1] };
                } else {
                    // Format: "day/month" - assume current year
                    return { day: parts[0], month: parts[1], year: new Date().getFullYear() };
                }
            } else if (parts.length === 3) {
                // Format: "day/month/year"
                return { day: parts[0], month: parts[1], year: parts[2] < 100 ? 2000 + parts[2] : parts[2] };
            }
            return { day: 0, month: 0, year: 0 };
        };
        
        const dateA = parseDate(a);
        const dateB = parseDate(b);
        
        if (dateA.year !== dateB.year) return dateA.year - dateB.year;
        if (dateA.month !== dateB.month) return dateA.month - dateB.month;
        return dateA.day - dateB.day;
    });
    
    chartData.labels = allDates;
    
    // Create a map for each metric: dateKey -> item
    // Only includes items where we can determine the year (from item or map)
    const createDateMap = (array) => {
        const map = new Map();
        if (array && Array.isArray(array)) {
            array.forEach(item => {
                if (item && (item.time !== null && item.time !== undefined)) {
                    const dateKey = getDateKey(item);
                    // Only add to map if we have a valid date key (i.e., we determined the year)
                    if (dateKey) {
                        map.set(dateKey, item);
                    }
                }
            });
        }
        return map;
    };
    
    // Build datasets by aligning data to the unified date labels
    filterKeys.forEach(key => {
        if (overviewData[key] && privateFilter[key]) {
            const dateMap = createDateMap(overviewData[key]);
            const values = allDates.map(dateKey => {
                const item = dateMap.get(dateKey);
                if (!item || (item.count === null || item.count === undefined)) {
                    return null; // Use null for missing data
                }
                return (key !== 'complaints_vs_visits') ? Math.round(+item.count) : +item.count;
            });
            
            const isAmended = key === 'visits' 
                ? allDates.map(dateKey => {
                    const item = dateMap.get(dateKey);
                    // Return blue if item exists and isAmended is true (check for boolean true, 1, or 'true')
                    const isItemAmended = item && (item.isAmended === true || item.isAmended === 1 || item.isAmended === 'true');
                    return isItemAmended ? 'blue' : 'white';
                })
                : undefined;
            
            const dataset = {
                label: key,
                data: values,
                barPercentage: 0.2,
            };
            if (key === 'visits' && isAmended) {
                dataset.isAmended = isAmended;
            }
            chartData.datasets.unshift(dataset);
        } else if (privateFilter[key] && key === 'complaints_vs_visits') {
            // For complaints_vs_visits, align both complaints and visits by date
            const complaintsMap = createDateMap(overviewData.complaints);
            const visitsMap = createDateMap(overviewData.visits);
            
            const values = allDates.map(dateKey => {
                const complaintItem = complaintsMap.get(dateKey);
                const visitItem = visitsMap.get(dateKey);
                
                if (!complaintItem || !visitItem || 
                    complaintItem.count === null || complaintItem.count === undefined ||
                    visitItem.count === null || visitItem.count === undefined ||
                    visitItem.count === 0) {
                    return null;
                }
                
                const complaintsVsVisits = complaintItem.count / visitItem.count;
                if (isNaN(complaintsVsVisits) || complaintsVsVisits === Infinity) return 0;
                return complaintsVsVisits;
            });
            
            chartData.datasets.unshift({ label: key, data: values });
        }
    });
    
    return chartData;
}



/**@param {Object} chartData * @param {Boolean} isShownTicks */
function getChartStyles(chartData, isShownTicks) {
    const { datasets } = chartData;
    let yAxesOptions = [];
    let chartTitles = [];

    if (isShownTicks) chartTitles = _getChartTitles(datasets);

    const datasetsWithStyle = datasets.map((dataset, index) => {
        const minMax = _getDatasetMinMax(dataset);
        const datasetId = utilService.generateId();

        dataset.yAxisID = datasetId;

        if (dataset.label === "avg_score") dataset = _assignBarColorsByScore(dataset);
        else dataset.type = "line";

        yAxesOptions[index] = _getYAxesItem(false, dataset, minMax, index, isShownTicks, datasetId, datasets.length);
        if (dataset.label === "avg_score") return dataset;
        else dataset = _overviewDatasetStyle(dataset);
        return dataset;
    });

    return { yAxesOptions, chartTitles, datasetsWithStyle };
}
function _assignBarColorsByScore(dataset) {
    dataset.backgroundColor = [];
    dataset.data.forEach(item => dataset.backgroundColor.push(_statusColor(+item)))
    return dataset;
}

function _getDatasetMinMax(dataset) {
    if (dataset.label !== 'avg_score') dataset.type = "line";
    return panelAxisMinMax(dataset.label, dataset.data);
}

/**
 * Y-axis limits used by the performance chart. The executive report calls this
 * with the same metric labels so a series fills the page the way it fills the panel.
 * @param {string} label
 * @param {Array<number|null>} data
 * @returns {{ min: number, max: number }}
 */
function panelAxisMinMax(label, data) {
    let minMax = utilService.findMaxAndMin(data);

    if (label === "avg_score") {
        minMax.min = 0;
        minMax.max = 100;
    } else if (label === "est_ROI" || label === "time_saved") {
        minMax.max = Math.round(minMax.max * 4);
        minMax.min = 0;
    } else if (label === "visits") {
        minMax.max = Math.round(minMax.max * 1.3);
        minMax.min = Math.round(minMax.min * 0.25);
    } else if (label === "complaints") {
        minMax.max = Math.round(minMax.max * 1.9);
        minMax.min = 0;
    } else if (label === "response_time_SLA") {
        const range = Math.max(0, minMax.max - minMax.min);
        const padding = Math.max(range * 0.2, 2);
        minMax.min = Math.max(0, Math.round(minMax.min - padding));
        minMax.max = Math.round(minMax.max + padding);
    } else if (label === "complaints_vs_visits") {
        const range = Math.max(0, minMax.max - minMax.min);
        const padding = Math.max(range * 0.2, 0.01);
        minMax.min = Math.max(0, minMax.min - padding);
        minMax.max = minMax.max + padding;
    } else if (label === "arrivals" || label === "unnecessary_arrivals") {
        minMax.max = Math.round(minMax.max * 1.3);
        minMax.min = 0;
    }
    return minMax
}

/**@param {Object} [dataset] @param {{min: Number; max:Number;}} [minMax] 
 * @param {Number} [index] @param {Boolean} [isShownTicks]
 * @param {String} [datasetId] @param {Number} [datasetsLength] 
 * @param {String} [positionOverride] Optional position override
 * @param {Boolean} isTrendLine*/
function _getYAxesItem(isTrendLine, dataset, minMax, index, isShownTicks, datasetId, datasetsLength) {
    if (isTrendLine) {
        return {
            gridLines: { color: "rgba(0, 0, 0, 0)" },
            position: 'left',
            ticks: { display: false, ...minMax }
        };
    } else {
        return {
            gridLines: { color: "rgba(0, 0, 0, 0)" },
            afterTickToLabelConversion: a => {
                for (var tick in a.ticks) {
                    a.ticks[tick] = utilService.kFormatter(a.ticks[tick]);
                }
            },
            position: index === 1 || datasetsLength === 1 ? "left" : "right",
            id: datasetId,
            ticks: {
                fontColor: chartColors[dataset.label].line,
                fontSize: 12,
                display: isShownTicks,
                ...minMax
            }
        }
    }
}

function _overviewDatasetStyle(dataset) {
    const pointRadius = dataset.data.length > 52 ? 5 : 6;
    dataset.pointHoverBackgroundColor = chartColors[dataset.label].line;
    dataset.pointHoverBorderColor = chartColors[dataset.label].line;
    dataset.pointHoverBorderWidth = 3;
    dataset.pointHoverRadius = pointRadius + 2;
    dataset.pointBorderColor = chartColors[dataset.label].line;
    dataset.pointBorderWidth = 3;
    dataset.pointHitRadius = pointRadius;
    dataset.pointRadius = pointRadius;
    // Set point background color: use isAmended array only for visits dataset, otherwise default to white
    dataset.pointBackgroundColor = (dataset.label === 'visits' && dataset.isAmended && Array.isArray(dataset.isAmended)) 
        ? dataset.isAmended 
        : "white";
    dataset.fill = false;
    dataset.borderColor = chartColors[dataset.label].line;
    dataset.borderWidth = 3;
    // Enable spanning gaps so lines connect across null/missing data points
    dataset.spanGaps = true;
    return dataset;
}


/**@typedef {{
 * x: Number;
 * y:Number;
 * }} Point
 * @param {Point[]} data
 * @returns {Function} */
function getTrendlineEq(data) {
    if (!data || data.length === 0) {
        return function trendline() { return 0; };
    }
    
    const xySum = data.reduce((acc, item) => {
        const xy = item.x * item.y
        acc += xy
        return acc
    }, 0)
    const xSum = data.reduce((acc, item) => {
        acc += item.x
        return acc
    }, 0)
    const ySum = data.reduce((acc, item) => {
        acc += item.y
        return acc
    }, 0)

    const aTop = (data.length * xySum) - (xSum * ySum)

    const xSquaredSum = data.reduce((acc, item) => {
        const xSquared = item.x * item.x
        acc += xSquared
        return acc
    }, 0)

    const aBottom = (data.length * xSquaredSum) - (xSum * xSum)

    // Guard against division by zero (happens when all x values are the same or all y values are the same)
    if (Math.abs(aBottom) < 1e-10) {
        // Return flat line at average y value
        const avgY = ySum / data.length;
        return function trendline() { return avgY; };
    }

    const a = aTop / aBottom
    const bTop = ySum - (a * xSum)
    const b = bTop / data.length

    // Check if coefficients are valid
    if (!isFinite(a) || !isFinite(b)) {
        const avgY = ySum / data.length;
        return function trendline() { return avgY; };
    }

    return function trendline(x) {
        return a * x + b
    }
}

/**@param {Number} score */
function _statusColor(score) {
    if (score === 100) return "#2D8144";
    else if (score > 97) return "#3B8B4D";
    else if (score > 94) return "#499657";
    else if (score > 91) return "#56A263";
    else if (score > 88) return "#64AB6B";
    else if (score > 85) return "#77B46B";
    else if (score > 82) return "#8CBC67";
    else if (score > 79) return "#A4C263";
    else if (score > 76) return "#BAC85D";
    else if (score > 73) return "#D1CD5E";
    else if (score > 70) return "#FAC058";
    else if (score > 67) return "#F8AE52";
    else if (score > 64) return "#F79E4F";
    else if (score > 61) return "#F48F4F";
    else if (score > 58) return "#EE824F";
    else if (score > 55) return "#E7734F";
    else if (score > 52) return "#E1654F";
    else if (score > 49) return "#D6544B";
    else if (score > 46) return "#CC4146";
    else return "#C23046";
}

function _getChartTitles(datasets) {
    return datasets.reduce((acc, dataset) => {
        acc.unshift({
            text: dataset.label,
            color: chartColors[dataset.label].line
        });
        return acc;
    }, [])
}

/**
 * @param {Number[]} nums 
 * @returns {{ start: Number; end: Number; }}
*/
function trendStartEnd(nums) {
    if (!nums || nums.length < 2) {
        // Return average if insufficient data for trend
        const avg = nums && nums.length === 1 ? nums[0] : 0;
        return { start: avg, end: avg };
    }
    
    const valuesForTrend = nums.map((a, index) => ({ x: index + 1, y: a }));
    const trendFunc = getTrendlineEq(valuesForTrend);
    
    // Check if trendFunc is valid (not NaN or Infinity)
    if (typeof trendFunc !== 'function') {
        const avg = nums.reduce((sum, val) => sum + val, 0) / nums.length;
        return { start: avg, end: avg };
    }
    
    // Evaluate within the observed domain to avoid extrapolation
    const start = trendFunc(1);
    const end = trendFunc(nums.length);
    
    // Validate results
    if (isNaN(start) || isNaN(end) || !isFinite(start) || !isFinite(end)) {
        const avg = nums.reduce((sum, val) => sum + val, 0) / nums.length;
        return { start: avg, end: avg };
    }
    
    return { start, end };
}

export class OverviewLineChartsData {
    /**@param {Object<string, { count:Number; time:String; }[]>} data */
    constructor(data) {
        const { complaints, response_time_SLA, time_saved, avg_score } = data;
        this.avg_score = avg_score;
        this.complaints = complaints;
        this.response_time_SLA = response_time_SLA;
        this.time_saved = time_saved;
    }

    /**@param {String} property */
    values(property) {
        return this[property].map(a => a.count !== null && +(a.count).toFixed(0))
    }

    /**
     * @param {String} property 
     * @param {Object<number,string>} [monthMap]
    */
    labels(property, monthMap) {
        if(monthMap) return this[property].map(a => monthMap[a.time]);
        return this[property].map(a => `${a.time < 10 ? "0" + a.time : a.time}${a.year ? `/${a.year.toString().slice(2, 4)}` : ""}`)
    }
}